/**
 * Cuánta de la pantalla se puede leer hoy. Es el encabezado de unit economics:
 * antes de mostrar 11 tarjetas, decir cuántas tienen dato.
 *
 * El número grande no es decorativo: es el que tiene que bajar. Los conteos salen
 * de las filas, así que cuando alguien carga CAC el bloque cambia solo.
 */
import { formatMetricValue, listaEs } from '@ops/contracts'

import {
  CLASE_KPI_VALOR,
  Card,
  Panel,
  ShareBar,
  Stack,
  StatusPill,
  type ShareSegment,
} from '../../ui'
import { segmentosDeUnEje } from '../comunes'
import type { Deuda, FilaMetrica } from './dependencias'

/**
 * Un solo eje: el porcentaje lo deriva el helper de la misma cantidad de
 * métricas que la leyenda imprime al lado (ver `modules/comunes/share.ts`).
 * Exportado para poder probar el invariante sobre el call site real.
 */
export function segmentosDeDeuda(deuda: Deuda): ShareSegment[] {
  return segmentosDeUnEje(
    [
      { id: 'con-dato', label: 'Con dato', cantidad: deuda.conDato, severidad: 'ok' },
      {
        id: 'sin-dato',
        label: 'Sin dato',
        cantidad: deuda.sinDato,
        severidad: 'unknown',
      },
    ],
    deuda.total,
  )
}

export interface DeudaDeDatosProps {
  filas: readonly FilaMetrica[]
  deuda: Deuda
}

export function DeudaDeDatos({ filas, deuda }: DeudaDeDatosProps) {
  const sinDato = filas.filter((f) => !f.conDato)
  const vencidas = filas.filter((f) => f.vencida)

  const segmentos: ShareSegment[] = segmentosDeDeuda(deuda)

  // Sin nada fuera de target no se dibuja un encabezado de acciones vacío.
  const pills =
    deuda.enRojo === 0 && deuda.enAtencion === 0 ? undefined : (
      <Stack direccion="horizontal" gap="sm" wrap align="center">
        {deuda.enRojo > 0 ? (
          <StatusPill severidad="critical" size="sm">
            {deuda.enRojo === 1
              ? '1 fuera de umbral'
              : `${deuda.enRojo} fuera de umbral`}
          </StatusPill>
        ) : null}
        {deuda.enAtencion > 0 ? (
          <StatusPill severidad="warning" size="sm">
            {deuda.enAtencion === 1
              ? '1 en atención'
              : `${deuda.enAtencion} en atención`}
          </StatusPill>
        ) : null}
      </Stack>
    )

  return (
    <Panel
      titulo="Deuda de datos"
      subtitulo={`Cuántas de las ${deuda.total} métricas de unit economics se pueden leer hoy`}
      {...(pills === undefined ? {} : { acciones: pills })}
    >
      <Stack gap="md">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-1">
          {/* No es `KpiValor`: la cifra es "N de M", dos números y una
              preposición, no un `MetricValue` formateado. La clase sí es la
              misma, que es lo que tiene que quedar alineado. */}
          <span data-testid="deuda-titular" className={CLASE_KPI_VALOR}>
            {deuda.sinDato} de {deuda.total}
          </span>
          <p className="max-w-prosa text-meta leading-relaxed text-muted-strong">
            {deuda.sinDato === 0 ? (
              <>
                Las {deuda.total} métricas tienen dato del período. Lo que queda es
                mantenerlas frescas.
              </>
            ) : (
              <>
                métricas sin dato:{' '}
                <b className="font-semibold text-text">
                  {listaEs(sinDato.map((f) => f.nombre))}
                </b>
                . Ninguna se puede leer del sheet original: nunca se cargaron.
              </>
            )}
          </p>
        </div>

        <ShareBar
          segmentos={segmentos}
          label={`Métricas de unit economics con dato, sobre ${deuda.total}`}
          total={deuda.total}
        />

        <Card padding="sm" hundida punteada>
          <p className="text-micro text-muted-strong">
            {vencidas.length === 0 ? (
              <>
                Ninguna carga manual está vencida. Una carga manual vence a los 30
                días: cuando pasa, aparece en ámbar y genera alerta en Overview.
              </>
            ) : (
              <>
                {vencidas.length === 1
                  ? 'Hay 1 carga vencida'
                  : `Hay ${vencidas.length} cargas vencidas`}{' '}
                (más de 30 días):{' '}
                <b className="font-semibold text-text">
                  {listaEs(
                    vencidas.map(
                      (f) =>
                        `${f.nombre} (${formatMetricValue(f.metric.valor, f.metric.unidad)})`,
                    ),
                  )}
                </b>
                . El dato está, pero no se puede defender en una reunión.
              </>
            )}
          </p>
        </Card>
      </Stack>
    </Panel>
  )
}
